import Util from 'util/util'

// Collection of util functions for input, textarea and contentEditable elemenets.
export default class InputUtil {

    static validateEmailInput(input = '') {
        if (!input || !Util.validateEmail(input)) {
            Backbone.View.layout.openStatus(window.i18n.gettext('You did not provide a valid email address.'))
            return false
        }
        return true
    }

    static validateURLInput(input = '') {
        if (!input || !Util.validateURL(input)) {
            Backbone.View.layout.openStatus(window.i18n.gettext('This is not a valid URL.'))
            return false
        }
        return true
    }

    static enterHTMLShortcut(e) {
        if (!InputUtil.isControlKey(e)) {
            return
        }

        let tag
        switch (e.keyCode) {
            case 66:
                tag = 'b'
                break
            case 73:
                tag = 'i'
                break
            case 85:
                tag = 'u'
                break
            case 187:
                tag = e.shiftKey ? 'sup' : 'sub'
                break
            default:
                return
        }

        e.stopPropagation()
        e.preventDefault()

        // Insert the HTML tag in the text
        const textarea = e.target
        const before = textarea.value.substr(0, textarea.selectionStart)
        const selection = textarea.value.substr(
            textarea.selectionStart,
            textarea.selectionEnd - textarea.selectionStart
        )
        const after = textarea.value.substr(textarea.selectionEnd)
        textarea.value = before + '<' + tag + '>' + selection + '</' + tag + '>' + after

        // Place the cursor after the closing tag (after the selection, 2 tag names, 5 characters such as <>)
        const cursorPosition = before.length + selection.length + (2 * tag.length) + 5
        textarea.setSelectionRange(cursorPosition, cursorPosition)

        // Trigger input event so Svelte can see the input value has changed.
        textarea.dispatchEvent(new Event('input', {bubbles: true}))
    }

    /**
     * Continue an ordered ("1. ") or unordered ("- " / "* ") list when the user hits return on a
     * line matching one of those patterns. Operates on plain text only, no HTML is inserted:
     * - Ordered lists: the next line gets the previous number plus one, e.g. "1. " becomes "2. ".
     * - Unordered lists: "-" or "*" is replaced by a bullet character, repeated on the next line.
     *
     * @param  {Event} e keydown event data.
     */
    static enterListPattern(e) {
        if (e.key !== 'Enter' || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) {
            return
        }

        const textarea = e.target
        const {value, selectionStart, selectionEnd} = textarea
        // Do nothing is selection is not collapsed.
        if (selectionStart !== selectionEnd) {
            return
        }

        const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1
        const nextNewline = value.indexOf('\n', selectionStart)
        const lineEnd = nextNewline === -1 ? value.length : nextNewline
        const line = value.substring(lineStart, lineEnd)

        // Find line starting with a digit followed by a period. Capture this digit to increment it by
        // one for the next line.
        const orderedListItem = line.match(/^(\d+)\. \S(?:.*\S)?$/)

        // Find line starting with -, * or •, followed by a space and then the rest of the line.
        const isUnorderedListItem = /^[-*•] \S(?:.*\S)?$/.test(line)

        let beforeInsertion
        if (orderedListItem) {
            const marker = `${parseInt(orderedListItem[1], 10) + 1}. `
            beforeInsertion = value.substring(0, selectionStart) + '\n' + marker
        } else if (isUnorderedListItem) {
            const bullet = '•'
            // Replace the '-' or '*' character at the start of the line with the bullet character.
            const contentBeforeCaret = value.substring(lineStart + 1, Math.max(selectionStart, lineStart + 1))
            beforeInsertion = value.substring(0, lineStart) + bullet + contentBeforeCaret + '\n' + bullet + ' '
        } else {
            return
        }

        e.stopPropagation()
        e.preventDefault()

        // Insert text at cursor position, then move cursor to end of inserted content.
        textarea.value = beforeInsertion + value.substring(selectionStart)
        const cursorPosition = beforeInsertion.length
        textarea.setSelectionRange(cursorPosition, cursorPosition)

        // Trigger input event so Svelte can see the input value has changed.
        textarea.dispatchEvent(new Event('input', {bubbles: true}))
    }

    static inputTabCharacter(e) {
        // Make it possible to enter tabs in the textarea.
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            const selectionStart = e.target.selectionStart
            e.target.value =
                e.target.value.substring(0, selectionStart) +
                '\t' +
                e.target.value.substring(e.target.selectionEnd)
            e.target.selectionEnd = selectionStart + 1
        }
    }

    static isControlKey(e) {
        // On Apple devices check if command key is pressed, otherwise check if control key is pressed
        return window.UAParser.os.is('macOS') || window.UAParser.os.is('iOS') ? e.metaKey : e.ctrlKey
    }

    /**
     * Put caret at the end of a content of input, textarea or content-editable element.
     *
     * @param  {Element} el
     * Input element
     * @param  {Boolean|undefined} selectAll
     * If true, set selection range to all instead of placing caret at the end
     */
    static placeCaret(el, selectAll) {
        el.focus()
        if (selectAll === true) {
            el.select()
        } else if (Number.isInteger(el.selectionStart)) {
            el.setSelectionRange(el.value.length, el.value.length)
        } else if (el.isContentEditable) {
            const selection = document.getSelection()
            selection.selectAllChildren(el)
            selection.collapseToEnd()
        }
    }

}
